import * as R from 'ramda';
import debugLib from 'debug';
import { reducerWrapper } from '../support/utils';
import { getPartnerName } from '../../main/components/PartnerManager/utils';
import type { AdPartnerReducer } from '../types/adPartnerTypes';
import {
  GET_PARTNERS,
  GET_PARTNER_FOR_ORG,
  CREATE_INTERNAL_AD_PARTNER,
  GET_AD_ONTOLOGY,
  SAVE_ENABLE,
  DISABLE,
  SELECT,
  UPDATE,
  SAVE_POSTBACK,
  RESET_SETTINGS,
  DELETE_INTERNAL_PARTNER,
  CHANGE_READY_FOR_SAVE,
  ROLLBACK_UNSAVED_CHANGES,
  GET_CUSTOM_PARTNERS_LIMIT,
} from '../actions/AdPartnerActions';
import { FB_SAVE_CONFIG } from '../actions/FacebookActions';
import { APPLE_SAVE_CONFIG } from '../actions/AppleSearchAdsActions';
import { GOOGLE_ADWORDS_SAVE, GET_GOOGLE_USER_INFO, REVOKE_GOOGLE_AUTH } from '../actions/GoogleActions';
const debug = debugLib('AdPartnerReducer');
export const INIT_STATE = {
  partners: [],
  orgPartners: {},
  selectedPartner: null,
  unsavedChanges: false,
  error: null,
  loading: false,
  accessToken: null,
  hasPartnersLoaded: false,
  mfReadyForSave: false,
  partnersBackup: [],
  customPartnerLimits: null,
  customPartnerLimitsLoading: false,
  customPartnerLimitsError: null,
};
export const sortByCompanyName = R.sortBy(
  R.compose(R.toLower, (partner) => {
    return getPartnerName(partner);
  })
);

function replacePartnerInPartnersList(partners, partner) {
  const { machine_name } = partner;
  const index = R.findIndex(R.propEq('machine_name', machine_name), partners);
  return [...partners.slice(0, index), partner, ...partners.slice(index + 1)];
}

// eslint-disable-next-line @typescript-eslint/default-param-last
function Handler(state: AdPartnerReducer = INIT_STATE, action) {
  debug('Receive action type', action.type);
  let partners, partner, enabledPartner, machine_name, index;

  switch (action.type) {
    case GET_PARTNERS.response:
      partners = sortByCompanyName(action.data.partners);
      const { selected } = action.data;
      const selectedPartner = selected ? R.find(R.propEq('machine_name', selected), partners) : state.selectedPartner;
      return R.merge(state, {
        partners,
        selectedPartner,
        loading: false,
        hasPartnersLoaded: true,
        partnersBackup: partners,
      });

    case CREATE_INTERNAL_AD_PARTNER.response:
      const internalPartner = action.data.selectedPartner;
      const partnersWithNewInternalPartner = [...state.partners, internalPartner];
      partners = sortByCompanyName(partnersWithNewInternalPartner);
      return R.merge(state, {
        partners,
        selectedPartner: internalPartner,
        loading: false,
        hasPartnersLoaded: true,
        partnersBackup: partners,
      });

    case GET_PARTNER_FOR_ORG.response:
      const partnerData = action.data;
      const orgPartners = { ...state.orgPartners, [partnerData.machine_name]: partnerData };
      return R.merge(state, {
        orgPartners,
        loading: false,
        hasPartnersLoaded: true,
      });

    case GET_AD_ONTOLOGY.response:
      return R.merge(state, {
        ad_ontology: action.data,
      });

    case REVOKE_GOOGLE_AUTH.response:
    case FB_SAVE_CONFIG.response:
    case GOOGLE_ADWORDS_SAVE.response:
    case APPLE_SAVE_CONFIG.response:
    // When we save FB we need to set it as saved and enabled in the
    // ad_partner store (but has to be triggered by separate action)
    case SAVE_ENABLE.response:
      machine_name = action.data.machine_name;
      partners = R.clone(state.partners);
      index = R.findIndex(R.propEq('machine_name', machine_name), partners);
      enabledPartner = R.merge(partners[index], action.data);
      partners = [...partners.slice(0, index), enabledPartner, ...partners.slice(index + 1)];
      return R.merge(state, {
        partners,
        selectedPartner: enabledPartner,
        unsavedChanges: false,
        loading: false,
        mfReadyForSave: false,
        partnersBackup: partners,
      });

    case DISABLE.response:
      machine_name = action.data.machine_name;
      index = R.findIndex(R.propEq('machine_name', machine_name), state.partners);
      const updatedPartner = R.clone(state.partners[index]);
      updatedPartner.enabled = false;
      partners = [...state.partners.slice(0, index), updatedPartner, ...state.partners.slice(index + 1)];
      return R.merge(state, {
        partners,
        selectedPartner: null,
        unsavedChanges: false,
        loading: false,
        partnersBackup: partners,
      });

    case SAVE_POSTBACK.response:
      /* service responds with entire updated partner object
       * we only want to update the new postback so we don't
       * overwrite any unsaved changes
       */
      partner = action.data.partner;
      const { event } = action.data;
      machine_name = partner.machine_name;
      const { postbacks } = partner;
      // As we now allow multiple postbacks for the same event (INTENG-5855),
      // we are going to assume that the last postback in the list for that
      // event is the new one.
      const newPostback = R.findLast(R.propEq('event', event), postbacks);
      index = R.findIndex(R.propEq('machine_name', machine_name), state.partners);
      const partnerFromList = R.clone(state.partners[index]);

      /**
       * This is a weird case after migration where ad partner entries were
       * migrated without postbacks. On the other hand, blueprint for
       * these ad partners have postbacks which is an object.
       * If an active ad partner doesn't have postbacks, we copy over
       * from blueprint as a object
       */
      partnerFromList.postbacks =
        partnerFromList.postbacks && Array.isArray(partnerFromList.postbacks)
          ? partnerFromList.postbacks.concat(newPostback)
          : [newPostback];
      partners = [...state.partners.slice(0, index), partnerFromList, ...state.partners.slice(index + 1)];
      return R.merge(state, {
        partners,
        selectedPartner: partnerFromList,
        loading: false,
        partnersBackup: partners,
      });

    case RESET_SETTINGS.response:
      partner = action.data.partnerBlueprint;
      const enabledPartners = action.data.enabledPartners;
      const selectedPartnerEnabled = R.hasIn(partner.machine_name, enabledPartners);

      // Check for org/app-level enablement when the ad network entries are reset
      if (!selectedPartnerEnabled) {
        partner.neverConfigured = true;
        partners = replacePartnerInPartnersList(state.partners, partner);
        return R.merge(state, {
          partners,
          unsavedChanges: false,
          loading: false,
          selectedPartner: partner,
          partnersBackup: partners,
        });
      }

      return R.merge(state, {
        unsavedChanges: false,
        loading: false,
      });

    case DELETE_INTERNAL_PARTNER.response:
      const updatedPartners = state.partners.filter((statePartner) => {
        return statePartner.machine_name !== action.data.partner.machine_name;
      });
      return R.merge(state, {
        partners: updatedPartners,
        unsavedChanges: false,
        loading: false,
        selectedPartner: null,
        partnersBackup: updatedPartners,
      });

    case SELECT:
      return R.merge(state, {
        selectedPartner: action.data,
      });

    case UPDATE:
      const { mfReadyForSave = false, ...config } = action.data;
      partners = replacePartnerInPartnersList(state.partners, config);
      return R.merge(state, {
        partners,
        selectedPartner: config,
        unsavedChanges: true,
        mfReadyForSave,
      });

    case ROLLBACK_UNSAVED_CHANGES:
      const selectedPartnerBackup = state.partnersBackup.find(
        (partnerBackup) => partnerBackup.machine_name === state.selectedPartner?.machine_name
      );
      return { ...state, partners: state.partnersBackup, selectedPartner: selectedPartnerBackup, unsavedChanges: false };

    case GET_GOOGLE_USER_INFO.response:
      partners = R.clone(state.partners);
      index = R.findIndex(R.propEq('machine_name', 'a_google_adwords'), partners);
      const credentialsWithUserInfo = R.merge(partners[index].credentials, action.data);
      enabledPartner = R.merge(partners[index], {
        credentials: credentialsWithUserInfo,
      });
      partners = [...partners.slice(0, index), enabledPartner, ...partners.slice(index + 1)];
      return R.merge(state, {
        partners,
        selectedPartner: enabledPartner,
        loading: false,
        partnersBackup: partners,
      });

    case CHANGE_READY_FOR_SAVE:
      return { ...state, mfReadyForSave: action.data };

    case GET_CUSTOM_PARTNERS_LIMIT.request:
      return {
        ...state,
        customPartnerLimitsError: null,
        customPartnerLimitsLoading: true,
      };

    case GET_CUSTOM_PARTNERS_LIMIT.response:
      return {
        ...state,
        customPartnerLimits: action.data,
        customPartnerLimitsLoading: false,
      };

    // TODO: Error handling in UI
    case GET_CUSTOM_PARTNERS_LIMIT.error:
      return {
        ...state,
        customPartnerLimitsError: action.data,
        customPartnerLimitsLoading: false,
      };

    default:
      return state;
  }
}

// @ts-ignore
const AdPartner = reducerWrapper(INIT_STATE, Handler, {
  request: [
    GET_PARTNERS,
    GET_PARTNER_FOR_ORG,
    SAVE_ENABLE,
    DISABLE,
    SELECT,
    UPDATE,
    SAVE_POSTBACK,
    RESET_SETTINGS,
    GET_GOOGLE_USER_INFO,
    GOOGLE_ADWORDS_SAVE,
    REVOKE_GOOGLE_AUTH,
    FB_SAVE_CONFIG,
    APPLE_SAVE_CONFIG,
    CHANGE_READY_FOR_SAVE,
    CREATE_INTERNAL_AD_PARTNER,
  ],
  error: [
    GET_PARTNERS,
    GET_PARTNER_FOR_ORG,
    SAVE_ENABLE,
    DISABLE,
    SELECT,
    UPDATE,
    SAVE_POSTBACK,
    RESET_SETTINGS,
    GET_GOOGLE_USER_INFO,
    GOOGLE_ADWORDS_SAVE,
    REVOKE_GOOGLE_AUTH,
    FB_SAVE_CONFIG,
    APPLE_SAVE_CONFIG,
    CHANGE_READY_FOR_SAVE,
    CREATE_INTERNAL_AD_PARTNER,
  ],
  resetOn: ['APP_CHANGE'],
});
export default AdPartner;
